import React from "react";

const PixelatedCanvas = ({
  src,
  width = 400,
  height = 400,
  cellSize = 4,
  dotScale = 0.9,
  shape = "square",
  backgroundColor = "transparent",
  grayscale = false,
  className = "",
  interactive = true,
  distortionStrength = 3,
  distortionRadius = 90,
  distortionMode = "swirl", 
  followSpeed = 0.15,
  dropoutStrength = 0.4,
  jitterStrength = 4,
  jitterSpeed = 4,
  sampleAverage = true,
  maxFps = 60,
}) => {
  const canvasRef = React.useRef(null);
  const samplesRef = React.useRef([]);
  const mouseTargetRef = React.useRef({ x: -9999, y: -9999 });
  const mouseRef = React.useRef({ x: -9999, y: -9999 });
  const pointerInsideRef = React.useRef(false);
  const animationFrameRef = React.useRef(null);
  const lastFrameRef = React.useRef(0);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !src) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let cancelled = false;
    let imageLoaded = false;

    canvas.width = width;
    canvas.height = height;

    function loop(timestamp) {
      if (cancelled) return;
      if (!imageLoaded) {
        animationFrameRef.current = requestAnimationFrame(loop);
        return;
      }

      const delta = timestamp - lastFrameRef.current;
      const minInterval = 1000 / maxFps;
      if (delta < minInterval) {
        animationFrameRef.current = requestAnimationFrame(loop);
        return;
      }
      lastFrameRef.current = timestamp;

      const samples = samplesRef.current;
      if (!samples || samples.length === 0) {
        animationFrameRef.current = requestAnimationFrame(loop);
        return;
      }

      const time = timestamp / 1000;

      ctx.save();

// limpiamos todo el canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

// solo pintamos fondo si NO es transparente
      if (backgroundColor && backgroundColor !== "transparent") {
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      const target = mouseTargetRef.current;
      const m = mouseRef.current;

      if (pointerInsideRef.current) {
        m.x += (target.x - m.x) * followSpeed;
        m.y += (target.y - m.y) * followSpeed;
      } else {
        m.x += (-9999 - m.x) * followSpeed;
        m.y += (-9999 - m.y) * followSpeed;
      }

      const maxJitter = jitterStrength;
      const dotSize = cellSize * dotScale;

      for (const s of samples) {
        let x = s.baseX;
        let y = s.baseY;

        // Distorsión por el puntero
        if (interactive && pointerInsideRef.current) {
          const dx = x - m.x;
          const dy = y - m.y;
          const dist = Math.hypot(dx, dy) || 1;

          if (dist < distortionRadius) {
            const t = 1 - dist / distortionRadius;

            if (distortionMode === "swirl") {
              const baseAngle = Math.atan2(dy, dx);
              const angle = baseAngle + distortionStrength * t;
              const radius = dist;
              x = m.x + Math.cos(angle) * radius;
              y = m.y + Math.sin(angle) * radius;
            } else {
              const factor = distortionStrength * t;
              x = x + (dx / dist) * factor * -distortionRadius * 0.2;
              y = y + (dy / dist) * factor * -distortionRadius * 0.2;
            }
          }
        }

        // Jitter (ruido) suave
        if (maxJitter > 0) {
          const noise = s.seed + time * jitterSpeed;
          x += Math.sin(noise) * maxJitter;
          y += Math.cos(noise * 1.2) * maxJitter;
        }

        let r = s.r;
        let g = s.g;
        let b = s.b;

        if (grayscale) {
          const l = 0.299 * r + 0.587 * g + 0.114 * b;
          r = g = b = l;
        }

        ctx.globalAlpha = s.a;
        ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;

        if (shape === "circle") {
          ctx.beginPath();
          ctx.arc(x, y, dotSize / 2, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillRect(
            x - dotSize / 2,
            y - dotSize / 2,
            dotSize,
            dotSize
          );
        }
      }

      ctx.restore();
      animationFrameRef.current = requestAnimationFrame(loop);
    }

    // Cargar la imagen y precalcular muestras
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = src;

    img.onload = () => {
      if (cancelled) return;
      imageLoaded = true;

      const tempCanvas = document.createElement("canvas");
      tempCanvas.width = width;
      tempCanvas.height = height;
      const tctx = tempCanvas.getContext("2d");
      if (!tctx) return;

      tctx.drawImage(img, 0, 0, width, height);
      const { data } = tctx.getImageData(0, 0, width, height);

      const samples = [];

      const sampleCell = (x0, y0) => {
        let r = 0,
          g = 0,
          b = 0,
          a = 0,
          count = 0;

        const maxX = Math.min(width, x0 + cellSize);
        const maxY = Math.min(height, y0 + cellSize);

        for (let y = y0; y < maxY; y++) {
          for (let x = x0; x < maxX; x++) {
            const idx = (y * width + x) * 4;
            const alpha = data[idx + 3];
            if (alpha === 0) continue;
            r += data[idx];
            g += data[idx + 1];
            b += data[idx + 2];
            a += alpha;
            count++;
          }
        }

        if (!count) return null;

        r /= count;
        g /= count;
        b /= count;
        a = (a / count) / 255;

        const luminance = (r + g + b) / (3 * 255);

        // dropout para zonas muy oscuras / planas
        if (luminance < dropoutStrength * 0.5) {
          if (Math.random() < dropoutStrength) return null;
        }

        return {
          baseX: x0 + cellSize / 2,
          baseY: y0 + cellSize / 2,
          r,
          g,
          b,
          a,
          seed: Math.random() * 1000,
        };
      };

      for (let y = 0; y < height; y += cellSize) {
        for (let x = 0; x < width; x += cellSize) {
          const sample = sampleAverage ? sampleCell(x, y) : sampleCell(x, y);
          if (sample) samples.push(sample);
        }
      }

      samplesRef.current = samples;

      lastFrameRef.current = performance.now();
      animationFrameRef.current = requestAnimationFrame(loop);
    };

    img.onerror = () => {
      console.error("PixelatedCanvas: could not load image", src);
    };

    function handlePointerMove(event) {
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;

      mouseTargetRef.current = {
        x: (event.clientX - rect.left) * scaleX,
        y: (event.clientY - rect.top) * scaleY,
      };
    }

    function handlePointerEnter(event) {
      pointerInsideRef.current = true;
      handlePointerMove(event);
    }

    function handlePointerLeave() {
      pointerInsideRef.current = false;
      mouseTargetRef.current = { x: -9999, y: -9999 };
    }

    if (interactive) {
      canvas.addEventListener("pointermove", handlePointerMove);
      canvas.addEventListener("pointerenter", handlePointerEnter);
      canvas.addEventListener("pointerleave", handlePointerLeave);
    }

    return () => {
      cancelled = true;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (interactive && canvas) {
        canvas.removeEventListener("pointermove", handlePointerMove);
        canvas.removeEventListener("pointerenter", handlePointerEnter);
        canvas.removeEventListener("pointerleave", handlePointerLeave);
      }
    };
  }, [
    src,
    width,
    height,
    cellSize,
    dotScale,
    shape,
    backgroundColor,
    grayscale,
    interactive,
    distortionStrength,
    distortionRadius,
    distortionMode,
    followSpeed,
    dropoutStrength,
    jitterStrength,
    jitterSpeed,
    sampleAverage,
    maxFps,
  ]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      aria-label="Pixelated rendering"
      role="img"
    />
  );
};

export default PixelatedCanvas;
